import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import {
  CreateProjectRequest,
  researchApi,
  ResearchProject,
} from "../../services/researchApi";

interface CreateProjectModalProps {
  open: boolean;
  onClose: () => void;
  onProjectCreated: (project: ResearchProject) => void;
}

const CreateProjectModal: React.FC<CreateProjectModalProps> = ({
  open,
  onClose,
  onProjectCreated,
}) => {
  const [projectName, setProjectName] = useState("");
  const [projectType, setProjectType] = useState<
    "tender" | "product_service" | "custom"
  >("custom");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!projectName.trim()) return;

    try {
      setCreating(true);
      setError(null);

      const data: CreateProjectRequest = {
        name: projectName.trim(),
        project_type: projectType,
        context: {
          schema_version: "1.0",
          created_from: "research_workflow_ui",
        },
      };

      const newProject = await researchApi.createProject(data);

      // Reset form
      setProjectName("");
      setProjectType("custom");

      // Notify parent and close
      onProjectCreated(newProject);
      onClose();
    } catch (err: any) {
      console.error("Failed to create project:", err);
      setError(err.response?.data?.message || "Failed to create project");
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    if (!creating) {
      setProjectName("");
      setProjectType("custom");
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#1e1e1e",
        },
      }}
    >
      <DialogTitle sx={{ backgroundColor: "#1e1e1e" }}>
        Create New Research Project
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: "#1e1e1e" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            fullWidth
            label="Project Name"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            required
            autoFocus
            placeholder="e.g., Defense Contractors Discovery"
            helperText="Enter a descriptive name for your research project"
          />

          <FormControl fullWidth>
            <InputLabel id="project-type-label">Project Type</InputLabel>
            <Select
              labelId="project-type-label"
              value={projectType}
              label="Project Type"
              onChange={(e) =>
                setProjectType(
                  e.target.value as "tender" | "product_service" | "custom",
                )
              }
            >
              <MenuItem value="tender">
                <Box>
                  <Typography variant="body2" fontWeight="600">
                    Tender / Bid
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Research around a specific procurement opportunity
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="product_service">
                <Box>
                  <Typography variant="body2" fontWeight="600">
                    Product / Service
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Research around a product, service, or market
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="custom">
                <Box>
                  <Typography variant="body2" fontWeight="600">
                    Custom
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Open-ended exploratory research
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#1e1e1e" }}>
        <Button onClick={handleClose} disabled={creating}>
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={!projectName.trim() || creating}
          startIcon={creating ? <CircularProgress size={16} /> : null}
        >
          {creating ? "Creating..." : "Create Project"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateProjectModal;
